<template>
  <div>
    <PageHeader
      :links="[
        { url: '/', label: 'Home' },
        { url: '/legal/privacy-policy', label: 'Privacy policy' },
        { url: '/docs', label: 'Docs' },
      ]"
    />
    <main>
      <PageBlock>
        <div class="head">
          <h1>Terms of Service</h1>
        </div>

        <div class="content">
          <p class="last-updated">Last updated April 4th, 2024</p>
          <p>
            THESE TERMS OF SERVICE (“TERMS OF SERVICE” OR “AGREEMENT”) GOVERN YOUR USE OF THE OTAGO.DEV WEBSITE AND/OR
            ANY OF THE SERVICES OFFERED BY DEVANCO, INC. (D/B/A “OTAGO”) (COLLECTIVELY, THE “SERVICES”). IN THESE TERMS
            OF SERVICE, OTAGO MAY BE REFERRED TO AS “OTAGO” OR “US”, “OUR” OR “WE”. BY ACCESSING, USING, SUBSCRIBING,
            PURCHASING, OR DOWNLOADING ANY OF THE SERVICES, YOU SIGNIFY THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE
            BOUND BY THESE TERMS OF SERVICE ON BEHALF OF YOURSELF AND/OR ON BEHALF OF THE ACCOUNT OWNER (REFERRED TO
            HEREIN AS “ACCOUNT OWNER”, “YOU”, OR “YOUR”). YOU AGREE TO AND WILL ENSURE THAT ALL WHO ACCESS THE SERVICE
            THROUGH YOUR ACCOUNT, FOLLOW AND BE BOUND BY THESE TERMS OF SERVICE. THESE TERMS OF SERVICE WILL APPLY TO
            YOUR USE OF THE SERVICES, WHETHER OR NOT YOU ARE A REGISTERED USER OF OUR SERVICES.
          </p>
          <p>
            IF YOU ARE ENTERING INTO THIS AGREEMENT ON BEHALF OF A COMPANY OR OTHER LEGAL ENTITY, YOU REPRESENT THAT YOU
            HAVE THE AUTHORITY TO BIND SUCH ENTITY, ITS AFFILIATES AND ALL USERS WHO ACCESS OUR SERVICES THROUGH YOUR
            ACCOUNT TO THESE TERMS AND CONDITIONS, IN WHICH CASE THE TERMS “YOU” OR “YOUR” SHALL REFER TO SUCH ENTITY,
            ITS AFFILIATES AND USERS ASSOCIATED WITH IT. IF YOU DO NOT HAVE SUCH AUTHORITY, OR IF YOU DO NOT AGREE WITH
            THESE TERMS OF SERVICE, YOU MUST NOT ACCEPT THIS AGREEMENT AND MAY NOT USE THE SERVICES.
          </p>
          <p>
            If Otago makes material changes to these Terms of Service, We will notify You by email or by posting a
            notice on Our site before the changes are effective. Any new features that augment or enhance the current
            Services, including the release of new tools and resources, shall be subject to these Terms of Service.
            Continued use of any of the Services after any such changes shall constitute your consent to such changes.
            You can review the most current version of the Terms of Service at any time at:
            <NuxtLink to="/legal/terms">https://otago.dev/legal/terms</NuxtLink>.
          </p>

          <h2>A. Definitions</h2>
          <ul>
            <li>
              <span class="font-semibold">Application</span>: means Your software program developed for use on multiple
              platforms, including mobile, web, and/or desktop and which are uploaded by You in connection with the
              Services.
            </li>
            <li>
              <span class="font-semibold">Account</span>: means an account through which You will access the Otago
              Services.
            </li>
            <li>
              <span class="font-semibold">Account Owner</span>: means the individual who establishes the Account
              (through the registration process provided by Us) and any other entity and/or person in whose name the
              Account is established, all of whom are agreed to be jointly and severally obligated under these Terms of
              Service.
            </li>
            <li>
              <span class="font-semibold">Usage Data</span>: means any information, content, data, comments, feedback
              (including, without limitation, schema and other implementation details, telemetry, IP and Geo-location
              data and service usage data) generated through Your use of the Services.
            </li>
            <li>
              <span class="font-semibold">User Data</span>: means all information, data or content owned by You and
              provided to Us by You in connection with Your use of the Services. User Data will not include any
              Application.
            </li>
            <li>
              <span class="font-semibold">Intellectual Property Rights</span>: means all patent rights, copyright
              rights, mask work rights, moral rights, rights of publicity, trademark, trade dress and service mark
              rights, goodwill, trade secret rights and other intellectual property rights as may now exist or hereafter
              come into existence, and all applications therefore and registrations, renewals and extensions thereof,
              under the laws of any state, country, territory or other jurisdiction.
            </li>
          </ul>

          <h2>B. Account Terms</h2>
          <ul>
            <li>
              You must be 18 years or older to use this Service. If you are under 18 years of age, you must have
              explicit consent from your parent or legal guardian in order to use this Service, including consent to use
              elements of this Service which collect data to make delivery of this Service possible.
            </li>
            <li>You must be a human. Accounts registered by “bots” or other automated methods are not permitted.</li>
            <li>
              You must provide your name, a valid email address, and any other information requested in order to
              complete the sign-up process for Your Account. All such information shall be accurate and complete.
            </li>
            <li>
              Your login may only be used by one person - a single login shared by multiple people is not permitted.
            </li>
            <li>
              You are responsible for maintaining the security of Your account and password. Otago cannot and will not
              be liable for any loss or damage from your failure to comply with this security obligation.
            </li>
            <li>
              You are responsible for all activity that occurs under your account (even when activity is from others who
              have accounts under your account).
            </li>
          </ul>

          <h2>C. Application and User Data</h2>
          <p>
            In connection with Your Application and/or User Data, You affirm, represent, and warrant that You own or
            have all necessary Intellectual Property Rights, licenses, consents, and permissions to use and authorize Us
            to use, retain, copy, and process the Application and/or User Data through the Services and as contemplated
            by this Agreement. You agree that by uploading, submitting or making any Application and/or User Data
            available to or through use of the Service, You hereby automatically at such time grant Otago, and it
            affiliates, for the duration of Your use of the Services, a non-exclusive, worldwide, royalty-free, license
            to use, reproduce, distribute, perform and display the Application and/or User Data solely for the purposes
            of providing the Services. You further agree Otago has the perpetual license and right to copy, analyze and
            use any of Your User Data solely in anonymized and aggregated format for Otago’s business purposes. For the
            avoidance of doubt, Otago will not; (i) reverse engineer, de-compile, translate, disassemble, or attempt to
            discover the source code of any Application or any part thereof; or (ii) distribute the Application or any
            part thereof to any third party other than solely as part of providing the Services. Otago does not
            pre-screen User Data, but Otago and its designee have the right (but not the obligation) in their sole
            discretion to refuse or remove any User Data that is available via the Services.
          </p>

          <h2>D. License Grant and Restrictions While Using the Services</h2>
          <p>
            Otago hereby grant to You, solely during the applicable term of Your use of the Services, a royalty free,
            limited, personal, non-exclusive, non-transferable, non-sub-licensable license to: (i) access (via the
            Internet address provided to You by Otago), download, install and use the Services; (ii) build and
            distribute to Your applications built with the Services (but without including or distributing the Services
            or any part thereof); and (iii) use the Documentation, training materials or other materials supplied by
            Otago to enable such licensed rights.
          </p>
          <p>You agree that You will not:</p>
          <ul>
            <li>
              Post, display or transmit information, data, Application(s) or User Data, including the unauthorized use
              of any payment method, that violates any law, regulation or rule, or the rights of any third party
              including without limitation Intellectual Property Rights;
            </li>
            <li>
              Post or transmit viruses, Trojan horses, worms, spyware, time bombs, cancelbots, or other computer
              programming routines that may harm the Services or interests or rights of other users, or that may harvest
              or collect any data or personally identifiable information about other users without their consent;
            </li>
            <li>
              Post or transmit any information, data, Application(s) or User Data that is unlawful, harmful, abusive,
              racially or ethnically offensive, defamatory, infringing, invasive of personal privacy or publicity
              rights, harassing, humiliating to other people (publicly or otherwise), libelous, threatening, or
              otherwise objectionable;
            </li>
            <li>
              Use or launch any automated system, including without limitation, “robots”, “spiders”, “offline readers”,
              etc., that accesses the Services in a manner that sends more request messages to the Otago servers than a
              human can reasonably produce in the same period of time by using a conventional on-line web browser; or
            </li>
            <li>
              Attempt to gain unauthorized access to any other party’s Account, password, Application or User Data, or
              allow more than one person to use an Account.
            </li>
            <li>Use the Services in any manner that would violate the terms of use of any applicable third-party.</li>
          </ul>

          <h2>E. Fees and Billing</h2>
          <p>
            You agree to provide current, complete, and accurate purchase and account information for all purchases of
            the Marketplace Offerings made via the Site. You further agree to promptly update account and payment
            information, including email address, payment method, and payment card expiration date, so that we can
            complete your transactions and contact you as needed. Sales tax will be added to the price of purchases as
            deemed required by us. We may change prices at any time. All payments shall be in U.S. dollars or Euros.
          </p>
          <p>
            You agree to pay all charges at the prices then in effect for your purchases and any applicable shipping
            fees, and you authorize us to charge your chosen payment provider for any such amounts upon placing your
            order. If your order is subject to recurring charges, then you consent to our charging your payment method
            on a recurring basis without requiring your prior approval for each recurring charge, until such time as you
            cancel the applicable order. We reserve the right to correct any errors or mistakes in pricing, even if we
            have already requested or received payment.
          </p>
          <p>
            We reserve the right to refuse any order placed through the Site. We may, in our sole discretion, limit or
            cancel quantities purchased per person, per household, or per order. These restrictions may include orders
            placed by or under the same customer account, the same payment method, and/or orders that use the same
            billing or shipping address. We reserve the right to limit or prohibit orders that, in our sole judgment,
            appear to be placed by dealers, resellers, or distributors.
          </p>

          <h2>F. Modifications to the Service and Prices</h2>
          <ul>
            <li>
              Otago reserves the right at any time and from time to time to modify or discontinue, temporarily or
              permanently, the Service (or any part thereof) with or without notice.
            </li>
            <li>
              Otago shall not be liable to you or to any third-party for any modification, price change, suspension or
              discontinuance of the Service.
            </li>
          </ul>

          <h2>G. Term and Termination</h2>
          <p>
            This Agreement shall remain in full force and effect while you use the Services. You may terminate the
            Services at any time by providing notice to Otago. Otago may permanently or temporarily terminate, suspend,
            or otherwise refuse to permit your access to the Services without notice and liability, if, in Our sole
            determination, You violate any of terms of this Agreement, including, but not limited to, the following
            prohibited actions: (i) attempting to interfere with, compromise the system integrity or security or
            decipher any transmissions to or from the servers running the Services; (ii) taking any action that imposes,
            or may impose at our sole discretion an unreasonable or disproportionately large load on our infrastructure;
            (iii) uploading invalid data, viruses, worms, or other software agents through the Service; (iv)
            impersonating another person or otherwise misrepresenting your affiliation with a person or entity,
            conducting fraud, hiding or attempting to hide your identity; (v) interfering with the proper working of the
            Service; or (vi) bypassing the measures we may use to prevent or restrict access to the Services, including,
            but not limited to, registering for the Services with an email address that is not rightfully yours.
          </p>
          <p>
            Upon termination of this Agreement or Your Account and hence the Services, all licenses granted by Us and
            rights to use the Services, will automatically terminate, and any Applications and all User Data in Your
            Account will be immediately deleted as of the date of termination or expiration of Your Account or this
            Agreement. You are responsible for requesting from Us the retrieval of any Applications and all User Data as
            You require or is required by law by contacting [<a href="mailto:contact@otago.dev">contact@otago.dev</a>]
            prior to the Account termination date. The following terms will survive any termination of this Agreement:
            Sections H, I, J, K and L.
          </p>

          <h2>H. Proprietary Rights</h2>
          <ul>
            <li>
              <span class="font-semibold">Services.</span> Except for your Application and User Data, the Services, and
              its materials, including, without limitation, software, images, text, graphics, illustrations, logos,
              patents, trademarks, service marks, copyrights, photographs, audio, videos and music and the Usage Data
              (the “Content”), and all Intellectual Property Rights related thereto, are the exclusive property of Otago
              and its licensors. Except as explicitly provided herein, nothing in this Agreement shall be deemed to
              create a license in or under any such Intellectual Property Rights, and You agree not to sell, license,
              rent, modify, distribute, copy, reproduce, transmit, publicly display, publicly perform, publish, adapt,
              edit or create derivative works from any materials or content accessible on the Service. Use of the
              Content, User Data or materials on the Services for any purpose not expressly permitted by this Agreement
              is strictly prohibited. You retain sole ownership of the Application and the User Data.
            </li>
            <li>
              <span class="font-semibold">Ideas and Comments.</span> You may choose to, or We may invite You to submit
              comments or ideas about the Services, including without limitation about how to improve the Services or
              our products (“Ideas”). By submitting any Idea, You agree that Your disclosure is gratuitous, unsolicited
              and without restriction and will not place Otago under any fiduciary or other obligation, that We are free
              to disclose the Ideas on a non-confidential basis to anyone or otherwise use the Ideas without any
              additional compensation to You. You acknowledge that, by acceptance of your submission, Otago does not
              waive any rights to use similar or related ideas previously known to Otago, or developed by its employees
              or contractors, or obtained from sources other than you.
            </li>
          </ul>

          <h2>I. Indemnity</h2>
          <p>
            You agree to defend, indemnify and hold harmless Otago and its affiliates, agents, employees, contractors,
            agents, officers and directors of each, from and against any and all claims, damages, obligations, losses,
            liabilities, costs or debt, and expenses (including but not limited to attorney’s fees) arising from: (i)
            Your use of and access to the Services; or (ii) Your violation of any terms of this Agreement.
          </p>

          <h2>J. Disclaimer</h2>
          <p>
            THE SERVICES ARE PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS. USE OF THE SERVICES ARE AT YOUR OWN RISK.
            THE SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT
            LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            WITHOUT LIMITING THE FOREGOING, OTAGO, ITS AFFILIATES, AND ITS LICENSORS DO NOT WARRANT THAT THE SERVICES
            ARE ACCURATE, RELIABLE OR CORRECT; THAT THE SERVICES WILL MEET YOUR REQUIREMENTS; THAT THE SERVICES WILL BE
            AVAILABLE AT ANY PARTICULAR TIME OR LOCATION, UNINTERRUPTED OR SECURE; THAT ANY DEFECTS OR ERRORS WILL BE
            CORRECTED; OR THAT THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>

          <h2>K. Limitation of Liability</h2>
          <p>
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY
            DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFIT,
            LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM YOUR USE OF THE SITE OR THE MARKETPLACE OFFERINGS,
            EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. NOTWITHSTANDING ANYTHING TO THE CONTRARY
            CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY CAUSE WHATSOEVER AND REGARDLESS OF THE FORM OF THE ACTION,
            WILL AT ALL TIMES BE LIMITED TO THE LESSER OF THE AMOUNT PAID, IF ANY, BY YOU TO US DURING THE SIX (6) MONTH
            PERIOD PRIOR TO ANY CAUSE OF ACTION ARISING OR $500.00 USD.
          </p>
          <p>
            THIS LIMITATION OF LIABILITY SECTION APPLIES WHETHER THE ALLEGED LIABILITY IS BASED ON CONTRACT, TORT,
            NEGLIGENCE, STRICT LIABILITY, OR ANY OTHER BASIS, EVEN IF OTAGO HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
            DAMAGE. THE FOREGOING LIMITATION OF LIABILITY SHALL APPLY TO THE FULLEST EXTENT PERMITTED BY LAW IN THE
            APPLICABLE JURISDICTION.
          </p>

          <h2>L. General Conditions</h2>
          <ul>
            <li>
              You understand that the technical processing and transmission of the Service, including your Content, may
              be transferred unencrypted and involve (a) transmissions over various networks; and (b) changes to conform
              and adapt to technical requirements of connecting networks or devices.
            </li>
            <li>
              If your bandwidth usage significantly exceeds the average bandwidth usage (as determined solely by Otago)
              of other Otago customers, we reserve the right to immediately disable your account or throttle your file
              hosting until you can reduce your bandwidth consumption.
            </li>
            <li>
              Otago does not warrant that (i) the service will meet your specific requirements, (ii) the service will be
              uninterrupted, timely, secure, or error-free, (iii) the results that may be obtained from the use of the
              service will be accurate or reliable, (iv) the quality of any products, services, information, or other
              material purchased or obtained by you through the service will meet your expectations, and (v) any errors
              in the Service will be corrected.
            </li>
            <li>
              Entire Agreement. This Agreement, together with any other legal notices and agreements published by Otago
              via the Services, shall constitute the entire agreement between you and Otago concerning the Services.
            </li>
            <li>
              No Waiver. Otago’s failure to assert any right or provision under this Agreement shall not constitute a
              waiver of such right or provision, and no waiver of any term of this Agreement shall be deemed a further
              or continuing waiver of such term or any other term.
            </li>
            <li>
              Headings. The section titles in this Agreement are for convenience only and have no legal or contractual
              effect.
            </li>
            <li>
              Severability. If any provision of this Agreement is unlawful, void or unenforceable by a court of
              competent jurisdiction, that provision is deemed severable from this Agreement and does not affect the
              validity and enforceability of any remaining provisions.
            </li>
            <li>
              Governing Law. You agree that the Agreement and Your use of the Services are governed under California
              law.
            </li>
          </ul>
          <p>
            Questions about the Terms of Service should be sent to
            <a href="mailto:contact@otago.dev">contact@otago.dev</a>
          </p>
        </div>
      </PageBlock>
    </main>
    <PageFooter />
  </div>
</template>

<script setup lang="ts">
useHead({
  title: "Otago - Terms of Service",
});
</script>

<style scoped>
.head {
  @apply py-4 lg:py-8 px-4 mx-auto max-w-screen-xl text-center;

  h1 {
    @apply mb-4 text-3xl font-extrabold tracking-tight leading-none md:text-5xl dark:text-blue-400;
  }
}

.content {
  @apply pt-6 pb-10 max-w-screen-md mx-auto text-white tracking-wide leading-relaxed;

  h2 {
    @apply mt-8 mb-4 text-2xl font-semibold dark:text-blue-400;
  }

  p {
    @apply my-3;
  }

  ul {
    @apply list-disc list-outside pl-5;

    li {
      @apply my-3;
    }
  }

  .last-updated {
    @apply italic mb-6;
  }
}
</style>
